import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LOOKUP_KIND_DEFS, LOOKUP_KIND_MAP, type LookupKind } from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, EmptyState, ErrorState, PageHead } from '../../components/ui';

interface LookupValue {
  id: string;
  kind: LookupKind;
  value: string;
  label: string;
  order: number;
  isActive: boolean;
}

/**
 * قوائم القيم.
 *
 * ما يُترك للشركة لا ما يفرضه النظام: مصدر العميل وسبب الخسارة والقسم
 * قوائم وصفية تختلف من شركة لأخرى. أما مرحلة الصفقة وحالة الفاتورة فتحكمها
 * قواعد عمل، ولا تُضاف مرحلة بكتابة اسمها في شاشة.
 */
export function Lookups() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<LookupKind>(LOOKUP_KIND_DEFS[0]!.key);
  const [adding, setAdding] = useState('');

  const canEdit = atLeast('ADMIN');
  const def = LOOKUP_KIND_MAP[kind];

  const values = useQuery({
    queryKey: ['lookups', kind],
    queryFn: () => api<LookupValue[]>(`/settings/lookups?kind=${kind}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['lookups'] });

  const add = useMutation({
    mutationFn: (label: string) =>
      api('/settings/lookups', { method: 'POST', body: { kind, label } }),
    onSuccess: () => {
      setAdding('');
      refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: (v: LookupValue) =>
      api(`/settings/lookups/${v.id}`, { method: 'PATCH', body: { isActive: !v.isActive } }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api<{ deactivated: boolean }>(`/settings/lookups/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const seed = useMutation({
    mutationFn: () => api<{ added: number }>('/settings/lookups/seed', { method: 'POST', body: { kind } }),
    onSuccess: refresh,
  });

  const rows = values.data ?? [];
  const active = rows.filter((r) => r.isActive);

  return (
    <div className="page">
      <PageHead title="قوائم القيم" subtitle="ما تختار منه شاشاتك" />

      <div className="toolbar">
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value as LookupKind)}
        >
          {LOOKUP_KIND_DEFS.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2 className="card-title">{def.label}</h2>
            <p className="tiny mute">
              {def.description} تظهر في: {def.usedIn}
            </p>
          </div>
          {canEdit && rows.length === 0 && (
            <button
              className="btn btn-sm"
              onClick={() => seed.mutate()}
              disabled={seed.isPending}
            >
              {seed.isPending ? 'جارٍ' : 'إضافة القيم المقترحة'}
            </button>
          )}
        </div>

        <div className="card-body">
          {values.isError && (
            <ErrorState error={values.error} onRetry={() => values.refetch()} />
          )}

          {add.isError && (
            <div className="alert alert-danger mb-4">
              {add.error instanceof Error ? add.error.message : 'تعذّرت الإضافة.'}
            </div>
          )}

          {remove.isSuccess && remove.data.deactivated && (
            /*
             * القيمة المستعملة تُعطَّل ولا تُحذف.
             *
             * السجلات تحمل القيمة نصًا؛ حذفها كان سيترك مئات السجلات تشير
             * إلى قيمة لا وجود لها في أي قائمة، فتظهر في التقارير معلَّقة.
             */
            <div className="alert alert-info mb-4">
              هذه القيمة مستعملة في سجلات قائمة، فعُطِّلت بدل حذفها. تختفي من قوائم الاختيار
              ويبقى ما سُجِّل بها كما هو.
            </div>
          )}

          {canEdit && (
            <div className="row mb-4" style={{ gap: 8 }}>
              <input
                className="input"
                value={adding}
                placeholder={`أضف إلى ${def.label}`}
                onChange={(e) => setAdding(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && adding.trim()) add.mutate(adding.trim());
                }}
              />
              <button
                className="btn btn-primary"
                onClick={() => add.mutate(adding.trim())}
                disabled={!adding.trim() || add.isPending}
              >
                إضافة
              </button>
            </div>
          )}

          {rows.length === 0 && !values.isLoading && (
            <EmptyState
              title="القائمة فارغة"
              text="أضف قيمك، أو ابدأ من القيم المقترحة وعدّلها."
            />
          )}

          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>القيمة</th>
                    <th>الحالة</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <tr key={v.id}>
                      <td className={v.isActive ? 'cell-strong' : 'cell-mute'}>{v.label}</td>
                      <td>
                        <Badge kind={v.isActive ? 'success' : 'neutral'}>
                          {v.isActive ? 'مفعّلة' : 'معطّلة'}
                        </Badge>
                      </td>
                      <td>
                        {canEdit && (
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button className="btn btn-sm" onClick={() => toggle.mutate(v)}>
                              {v.isActive ? 'تعطيل' : 'تفعيل'}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => remove.mutate(v.id)}
                            >
                              حذف
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.length > 0 && (
            <p className="tiny mute mt-3">
              {active.length} قيمة مفعّلة من {rows.length}. المعطّلة تختفي من قوائم الاختيار
              ولا تُمسّ السجلات المسجَّلة بها.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
